/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from "react";
import { TextField } from "@mui/material";
import { canEdit, titleFormat } from "@iventis/utilities";
import { DataField } from "@iventis/domain-model/model/dataField";
import { FieldConfig, useField } from "formik";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { DataFieldValueTypes } from "@iventis/types";
import { media, styled } from "@iventis/styles";
import { AutocompleteWithLoading } from "@iventis/components";
import { StyledFieldLabel } from "@iventis/styles/src/components/forms";
import { scheduleApi } from "@iventis/api/src/api";
import { ScheduleVersionNode } from "./schedule.types";

interface DataFieldComponentProp extends FieldConfig {
    scheduleNodeVersions: ScheduleVersionNode[];
    dataField: DataField;
    isScheduleManager: boolean;
    dataFieldValue: DataFieldValueTypes;
    disabled: boolean;
    setDataFieldValue: (val) => void;
}

/**
 * This component handles a single DataField object, which in this context represents the type of subschedule a session
 * will be part of.
 * It is designed to be "plugged-in" to a formik form, and it will take its initial selected value from it;
 * It contains 1 read-only input which reflects the value chosen from the second input of type "Select(Autocomplete)"; the latter is only available
 * if the user has Schedule Manager permission/role; else, no selection will be offered and only the read-only input will be shown.
 * The Select input will have a pre-selected data field value which is handled entirely by the parent component,
 * (which in this context represents the initial subschedule the user chooses to create a session on)
 */
const SubScheduleSelector: React.FunctionComponent<DataFieldComponentProp> = ({
    dataField,
    scheduleNodeVersions,
    isScheduleManager,
    dataFieldValue,
    setDataFieldValue,
    disabled,
    ...props
}) => {
    const { name } = props;
    const [field, meta, helpers] = useField(name);

    // Fetch the schedule version to get its data field value
    useEffect(() => {
        if (field.value?.id) {
            const fetchVersionFromScheduleService = async () => {
                const res = await scheduleApi.get<ScheduleVersion>(`/schedule-versions/${scheduleNodeVersions.find((v) => v.id === field.value.id).id}`);
                const scheduleVersion = res.data;
                const dataFieldValue = scheduleVersion.dataFieldValues.find((dfv) => dfv.dataField.id === dataField.id)?.value;
                setDataFieldValue(dataFieldValue);
            };
            fetchVersionFromScheduleService();
        }
    }, [field.value?.id]);

    return (
        <StyledContainer>
            <div className="inner-box">
                <StyledFieldLabel>{titleFormat(dataField.name)}</StyledFieldLabel>
                <TextField
                    variant="outlined"
                    value={dataFieldValue ? `${(dataFieldValue as DataFieldListItem)?.metaData?.code} - ${(dataFieldValue as DataFieldListItem)?.name}` : ""}
                    inputProps={{ readOnly: true }}
                />
            </div>
            {isScheduleManager && (
                <div className="inner-box">
                    <StyledFieldLabel>{titleFormat(dataField.name)} Schedule</StyledFieldLabel>
                    <AutocompleteWithLoading
                        value={field.value || null}
                        disabled={disabled}
                        options={scheduleNodeVersions.filter((node) => canEdit(node.authorisation))}
                        getOptionLabel={(option: DataFieldListItem) => option.name}
                        getOptionSelected={(option: DataFieldListItem, value: DataFieldListItem) => {
                            if (value.id === null || value.id === "") return false;
                            if (value.id === option.id) return true;
                            return false;
                        }}
                        loadingOptions={false}
                        onChange={(value: number) => {
                            helpers.setValue(value, true);
                        }}
                        error={!!(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error}
                    />
                </div>
            )}
        </StyledContainer>
    );
};
const StyledContainer = styled.div`
    display: flex;
    gap: 8%;
    .inner-box {
        width: 46%;
        display: flex;
        flex-direction: column;
    }
    ${media.extraSmall} {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        .inner-box {
            width: 100%;
        }
    }
`;
export default SubScheduleSelector;
